// @flow
import * as React from 'react';
import { useContext } from 'react';
import { Content } from '@data/contents/Contents.model';
import Skeleton from '@mui/material/Skeleton';
import UseConfirmModal from '@hooks/confirm-modal-hook/UseConfirmModal';
import { IconButton } from '@mui/material';
import useIsMobile from '@hooks/use-breakpoint/useIsMobile';
import { useContentSelect } from '@view/content-management-system/context/ContetSelectContext';
import { ContentListFilterContext } from '@view/content-management-system/context/ContentListFilterContext';
import { ReactComponent as ArticleIcon } from '@assets/icons/content/article.svg';
import Icon from '@shared/components/icon';
import SvgIcons from '../../../models/constants/svgIcons';
import useContent from '../hooks/useContent';

type ContentItemProps = {
  content?: Content;
  loading?: boolean;
};

const getColumnWidth = (ratio: number) => ({
  width: `${ratio * 100}%`,
});

const ContentItem: React.FC<ContentItemProps> = ({ content, loading }) => {
  const { title, createdAt, contentLabelType, onContentClick, action } =
    useContent(content);

  const { selectedContents, toggleSelectContent } = useContentSelect();
  const contentId = content?.id;

  const isMobile = useIsMobile();

  const { sorts } = useContext(ContentListFilterContext) ?? {};

  const typeRatio = sorts?.options[0]?.ratio ?? 0.12;
  const titleRatio = sorts?.options[1]?.ratio ?? 0.58;
  const dateRatio = sorts?.options[2]?.ratio ?? 0.1;

  const [ConfirmationModal, showModal, hideModal] = UseConfirmModal({
    approveButton: {
      label: action.label,
      action: () => {
        action.onClick().then(() => hideModal());
      },
    },
    description: `Are you sure you want to ${action.label.toLowerCase()} this content?`,
    hasLoading: true,
    rejectButton: {
      label: 'Cancel',
    },
    title: `${action.label.toLowerCase()} content?`,
  });

  const renderItemType = React.useCallback(() => {
    if (!contentLabelType) return null;
    const iconNode = contentLabelType.icon?.('w-[14px] h-[14px] shrink-0') ?? (
      <ArticleIcon className="w-[14px] h-[14px] shrink-0" />
    );
    return (
      <div
        className="flex items-center h-full pl-1 overflow-hidden shrink-0"
        style={getColumnWidth(typeRatio)}
      >
        {iconNode}
        <span className="ml-2 uppercase truncate caption text-on-primary">
          {contentLabelType.label}
        </span>
      </div>
    );
  }, [contentLabelType, typeRatio]);

  const renderCommunityNotifyIcon = React.useCallback(() => {
    const isNotified = content?.communityNotified === true;

    return (
      <span
        className="inline-flex items-center justify-center bg-yellow-200 aspect-square h-[18px] w-[18px]"
        aria-label={
          isNotified ? 'Community notified' : 'Community not notified'
        }
      >
        <Icon
          iconName={
            isNotified ? SvgIcons.NOTIFICATIONS : SvgIcons.NOTIFICATIONS_OFF
          }
          className="aspect-square h-[18px] w-[18px] shrink-0"
        />
      </span>
    );
  }, [content?.communityNotified]);

  if (loading)
    return (
      <Skeleton
        variant="rectangular"
        className="mb-[6px]"
        width="100%"
        height={60}
      />
    );

  return (
    <div className="relative">
      <ConfirmationModal />
      <IconButton
        className={`mb-[6px] flex h-[60px] w-full items-center justify-start rounded-none bg-white pl-5 pr-6 py-0
        ${isMobile ? 'flex-col items-stretch' : 'flex-row'}
        `}
        sx={{ alignItems: 'center' }}
        onClick={onContentClick}
      >
        <div className="flex items-center justify-center w-10 h-full shrink-0">
          <input
            type="checkbox"
            className="w-4 h-4"
            checked={
              contentId ? selectedContents?.includes?.(contentId) : false
            }
            onClick={(e) => {
              e.stopPropagation();
              if (contentId) toggleSelectContent(contentId);
            }}
          />
        </div>
        {renderItemType()}
        <div className="flex items-center justify-center w-6 h-full shrink-0">
          {renderCommunityNotifyIcon()}
        </div>
        <div
          className="flex items-center justify-start h-full min-w-0 text-left shrink-0"
          style={getColumnWidth(titleRatio)}
        >
          <span
            className="w-full min-w-0 text-black truncate caption-medium"
            title={title}
          >
            {isMobile ? title?.substring(0, 40) : title}
          </span>
        </div>
        <span
          className={`flex h-full shrink-0 items-center italic caption ${
            isMobile ? 'w-full text-left' : 'text-left'
          }`}
          style={getColumnWidth(dateRatio)}
        >
          {createdAt}
        </span>
      </IconButton>
    </div>
  );
};

ContentItem.defaultProps = {
  content: undefined,
  loading: false,
};
export default ContentItem;
